import { ReportAssumption } from '@futrli-sdk/predict';
import { joinUniqId } from '@futrli/shared-services';
import { merge } from 'lodash';

import { ASSUMPTION_TYPES } from '../../../../core/constants';
import { stringPenceToAmount } from '../../../../core/formatters';
import { AssumptionData } from '../../../../core/types';
import { ROW_KIND } from '../data-grid/constants';
import { RowType } from '../data-grid/types';
import {
  CellEditingMode,
  RowCellEditingMode,
  RowEditingMode,
  RowHeights,
  UserGridSettings
} from './types';
import { userGridSettingsPersistor } from './user-grid-settings-persistor';

export const persistUserGridSettings = (payload: Partial<UserGridSettings>) => {
  userGridSettingsPersistor.persist(payload);
};

export const addExpandedRow = (rows: UserGridSettings['expandedRows'], rowId: string): [UserGridSettings['expandedRows'], boolean] => {
  const foundRow = rows.find(rId => rId === rowId);

  return foundRow
    ? [rows, false]
    : [rows.concat(rowId), true];
}

export const addEditingRow = (rows: RowEditingMode, forecastId: string, parentRowId: string, rowId: string): [RowEditingMode, boolean] => {
  const uniqRowId = joinUniqId(forecastId, parentRowId, rowId);

  const foundRow = rows.find(rId => rId === uniqRowId);

  return foundRow
    ? [rows, false]
    : [rows.concat(uniqRowId), true];
};

export const removeEditingRow = (rows: RowEditingMode, forecastId: string, parentRowId: string, rowId: string): [RowEditingMode, boolean] => {
  const uniqRowId = joinUniqId(forecastId, parentRowId, rowId);
  const updatedRows = rows.filter(rId => rId !== uniqRowId);

  return (
    !updatedRows || updatedRows.length === rows.length
      ? [rows, false]
      : [updatedRows, true]
  );
};

export const isRowInEditingMode = (rows: RowEditingMode, forecastId: string, row: RowType): boolean => {
  const { index, type } = row.account;

  switch(type) {
    case ROW_KIND.account: {
      const uniqRowId = joinUniqId(forecastId, index);
      const foundRow = rows.find(rId => rId.includes(uniqRowId));
      return !!foundRow;
    }
    case ASSUMPTION_TYPES.BASIC: {
      const { parent_row_id } = row.account as ReportAssumption;
      const uniqRowId = joinUniqId(forecastId, parent_row_id, index);
      const foundRow = rows.find(rId => rId === uniqRowId);
      return !!foundRow;
    }
    default:
      return false;
  }
};

export const updateEditingRowCells = (
  rows: RowCellEditingMode, forecastId: string, parentRowId: string, rowId: string, cellData: CellEditingMode
): [RowCellEditingMode, boolean] => {
  const uniqRowId = joinUniqId(forecastId, parentRowId, rowId);

  const data = { [uniqRowId]: { ...cellData } };

  const updatedRowCells = merge(rows, data);

  return [updatedRowCells, true];
};

export const removeEditingRowCells = (
  rows: RowCellEditingMode, forecastId: string, parentRowId: string, rowId: string
): [RowCellEditingMode, boolean] => {
  const uniqRowId = joinUniqId(forecastId, parentRowId, rowId);
  const foundRow = rows[uniqRowId];

  if (!foundRow) {
    return [rows, false];
  }

  const cells = Object.keys(foundRow ?? {});

  if (!cells.length) {
    return [rows, false];
  }

  delete rows[uniqRowId];

  return [rows, true];
};

export const denormalizeEditingRowCells = (cells: CellEditingMode): AssumptionData[] => {
  return Object.entries(cells).map(([key, value]) => ({ date: key, amount: stringPenceToAmount(value) }));
}

export const getRowHeightSize = (rowHeights: RowHeights) => {
  return rowHeights.default;
}
